import { GeekFullName, PublicPathImage, Loader, InputValidationInfo } from '@/components';
import { formatDate, formatDateTime, withLinks, getNullSafeValue, enableTooltip, substring } from '@/filters';
import { UserStatus } from '@/models';
import { maxTagNameValidator, notPastDateValidator } from '@/validators';
import PermissionsPlugin from '@/shared/permissions.plugin';
import BootstrapVue, {
  CollapsePlugin,
  DropdownPlugin,
  FormPlugin,
  ModalPlugin,
  VBScrollspyPlugin,
  VBTooltip,
} from 'bootstrap-vue';
import Vue from 'vue';
import VeeValidate, { Validator } from 'vee-validate';
import Toasted from 'vue-toasted';
import VueAwesomeSwiper from 'vue-awesome-swiper';
import VueAppInsights from 'vue-application-insights';
import Croppa from 'vue-croppa';
import Clipboard from 'v-clipboard';
import moment from 'moment';
import numeral from 'numeral';
import { VueRouter } from 'vue-router/types/router';
import App from './App.vue';
import { focusDatepickerInput } from './directives';
import VueI18n from './i18n';
import { englishValidationsDictionary } from './locales/validation';
import { RouterFactory } from './router';
import { Actions, RootStore } from './store';
import { userGuard, permissionsGuard } from './guards';

Vue.config.devtools = process.env.NODE_ENV === 'development';
Vue.config.productionTip = false;

Vue.component('GeekFullName', GeekFullName);
Vue.component('Loader', Loader);
Vue.component('PublicPathImage', PublicPathImage);
Vue.component('InputValidationInfo', InputValidationInfo);

Vue.filter('formattedDate', formatDate);
Vue.filter('formattedDateTime', formatDateTime);
Vue.filter('withLinks', withLinks);
Vue.filter('enableTooltip', enableTooltip);
Vue.filter('nullSafe', getNullSafeValue);
Vue.filter('substring', substring);

Validator.extend('maxTagName', maxTagNameValidator);
Validator.extend('notPastDate', notPastDateValidator);

Vue.directive('b-tooltip', VBTooltip);
Vue.directive('focusDatepickerInput', focusDatepickerInput);

Vue.use(BootstrapVue as any);
Vue.use(CollapsePlugin);
Vue.use(DropdownPlugin);
Vue.use(FormPlugin);
Vue.use(ModalPlugin);
Vue.use(VBScrollspyPlugin);
Vue.use(VueAwesomeSwiper);
Vue.use(Croppa);
Vue.use(Clipboard);
Vue.use(
  Toasted,
  { duration: 8000, position: 'bottom-right' } as unknown as undefined,
);

Vue.use(VeeValidate, {
  i18nRootKey: 'validations',
  VueI18n,
  dictionary: {
    en: englishValidationsDictionary,
  },
});

Vue.use(PermissionsPlugin);

numeral.register('locale', 'pl', {
  delimiters: {
    thousands: ' ',
    decimal: ',',
  },
  abbreviations: {
    thousand: 'tys.',
    million: 'mln',
    billion: 'mld',
    trillion: 'bln',
  },
  ordinal(): string {
    return '.';
  },
  currency: {
    symbol: 'PLN',
  },
});

moment.locale(process.env.VUE_APP_I18N_LOCALE || 'pl');
numeral.locale(process.env.VUE_APP_I18N_LOCALE || 'pl');

export const SELECT_SEARCH_CHAR_THRESHOLD = 3;

export let router: VueRouter;

export let appInsightsInstrumentationKey: string;

const INACTIVE_USER_PAGES: string[] = [
  'kitchen-tv',
  'account-sign-in',
  'account-registration',
  'account-forgot-password',
  'account-reset-password',
  'account-change-email',
  'account-confirm-email',
];

function useActiveUserGuards() {
  router.beforeEach((...routerGuardParams) => {
    userGuard(...routerGuardParams);
    permissionsGuard(...routerGuardParams);
  });
}

function useInactiveUserGuards() {
  router.beforeEach((...routerGuardParams) => {
    if (routerGuardParams.find(x => x.name && INACTIVE_USER_PAGES.indexOf(x.name) >= 0)) {
      permissionsGuard(...routerGuardParams);
      return;
    }

    userGuard(...routerGuardParams);
  });
}

new Promise<void>(async (resolve) => {
  await RootStore.dispatch(Actions.themes.fetchCurrentTheme);
  await RootStore.dispatch(Actions.geek.fetchCsrfToken);

  const status = await RootStore.dispatch(Actions.users.fetchCurrentUserStatus) as UserStatus;

  if (status !== UserStatus.Active) {
    router = RouterFactory.build();
    useInactiveUserGuards();
    return resolve();
  }

  const geek = await RootStore.dispatch(Actions.geek.fetchCurrent);

  router = RouterFactory.build(geek.localization);
  appInsightsInstrumentationKey = await RootStore.dispatch(Actions.users.fetchAppInsightsInstrumentationKey);

  useActiveUserGuards();
  resolve();
}).finally(() => {
  Vue.use(VueAppInsights, {
    id: appInsightsInstrumentationKey,
    router,
  });

  mountApp();
});

function mountApp() {
  new Vue({
    store: RootStore,
    router,
    i18n: VueI18n,
    render: (h) => h(App),
  }).$mount('#app');
}
